import { useEffect, useState } from "react";
import { Download, FileUp, Instagram, Loader2, MessageCircle, Radar, RefreshCw, Sparkles, Target, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const STAGES = ["new", "contacted", "responded", "completed", "closed"];
const STAGE_STYLES = {
  new: "bg-[#D4AF37]/15 text-[#8a6d1f]",
  contacted: "bg-[#E8D5CE] text-[#5A504B]",
  responded: "bg-green-100 text-green-800",
  completed: "bg-emerald-100 text-emerald-800",
  closed: "bg-gray-200 text-gray-500",
};

const daysSince = (iso) => {
  try {
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  } catch {
    return null;
  }
};

const LeadCard = ({ lead }) => {
  const [drafting, setDrafting] = useState(false);
  const phoneDigits = (lead.phone || "").replace(/[^\d]/g, "");
  const days = daysSince(lead.created_at);

  const aiFollowup = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/leads/followup-draft", { enquiry_id: lead.id }, { timeout: 60000 });
      window.open(`https://wa.me/${(data.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(data.message)}`, "_blank");
      toast.success("AI follow-up drafted — review and send in WhatsApp");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Drafting failed — try again");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="glass flex flex-wrap items-center gap-4 rounded-2xl p-5" data-testid={`lead-card-${lead.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-[#1A1615]">{lead.name}</p>
          <span className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ${STAGE_STYLES[lead.status] || STAGE_STYLES.new}`}>
            {lead.status || "new"}
          </span>
          {lead.source && (
            <span data-testid={`lead-source-${lead.id}`} className="inline-flex items-center gap-1 rounded-full border border-[#E8D5CE] px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-[#91837C]">
              {lead.source === "instagram" && <Instagram className="h-2.5 w-2.5" />}
              {lead.source.replace(/_/g, " ")}
            </span>
          )}
        </div>
        <p className="mt-1 truncate text-xs font-light text-[#91837C]">
          {lead.treatment || "General consultation"}
          {lead.preferred_date ? ` · ${lead.preferred_date}${lead.preferred_time ? ` ${lead.preferred_time}` : ""}` : ""}
          {days !== null ? ` · ${days === 0 ? "today" : `${days}d ago`}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={aiFollowup}
          disabled={drafting || !phoneDigits}
          data-testid={`lead-ai-draft-${lead.id}`}
          className="btn-gold inline-flex items-center gap-1.5 px-4 py-2 text-[10px] disabled:opacity-50"
        >
          {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          AI Follow-Up
        </button>
        {phoneDigits && (
          <a
            href={`https://wa.me/${phoneDigits}?text=${encodeURIComponent(`Dear ${lead.name}, following up from Royal Skin Clinic regarding your ${lead.treatment || "consultation"} enquiry — `)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`lead-whatsapp-${lead.id}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform hover:-translate-y-0.5"
            aria-label={`WhatsApp ${lead.name}`}
          >
            <MessageCircle className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};

// Lead Generation command centre: pipeline, AI outreach, demand radar, analytics.
export const LeadsTab = () => {
  const { content } = useContent();
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [radar, setRadar] = useState(null);
  const [stage, setStage] = useState("all");
  const [radarBusy, setRadarBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importSource, setImportSource] = useState("manual");
  const [importBusy, setImportBusy] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const load = async () => {
    try {
      const [{ data: enq }, { data: stats }] = await Promise.all([api.get("/admin/enquiries"), api.get("/admin/leads/analytics")]);
      setLeads(enq);
      setAnalytics(stats);
    } catch {
      toast.error("Could not load leads");
    }
  };

  useEffect(() => {
    load();
    setRadar(content?.settings?.demand_radar || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const runRadar = async () => {
    setRadarBusy(true);
    try {
      const { data } = await api.post("/admin/leads/demand-radar", {}, { timeout: 90000 });
      setRadar(data);
      toast.success("Demand radar refreshed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Radar failed — try again");
    } finally {
      setRadarBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "royal-skin-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV");
    } catch {
      toast.error("Export failed");
    }
  };

  const doImport = async () => {
    if (!importText.trim()) {
      toast.error("Paste or upload some leads first");
      return;
    }
    setImportBusy(true);
    try {
      const { data } = await api.post("/admin/leads/import", { csv_text: importText, source: importSource });
      toast.success(`Imported ${data.imported} lead${data.imported === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped as duplicates` : ""}`);
      setImportOpen(false);
      setImportText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed — check the format");
    } finally {
      setImportBusy(false);
    }
  };

  const scanInstagram = async () => {
    setScanBusy(true);
    setScanResult(null);
    try {
      const { data } = await api.post("/admin/social/instagram/scan", {}, { timeout: 60000 });
      setScanResult(data);
      if (data.scanned) {
        toast.success(data.new_leads ? `${data.new_leads} new lead${data.new_leads === 1 ? "" : "s"} captured from Instagram` : "Scan complete — no new enquiry-intent comments");
        if (data.new_leads) load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Scan failed — try again");
    } finally {
      setScanBusy(false);
    }
  };

  const filtered = stage === "all" ? leads : leads.filter((l) => (l.status || "new") === stage);
  const maxDaily = Math.max(1, ...(analytics?.daily || []).map((d) => d.count));

  return (
    <div className="space-y-8" data-testid="leads-tab">
      {/* analytics strip */}
      <div className="glass rounded-2xl p-6" data-testid="leads-analytics">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1615]">
            <Users className="h-5 w-5 text-[#B38B2D]" /> Lead Analytics
          </h2>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setImportOpen(true)} data-testid="leads-import-button" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px]">
              <FileUp className="h-3.5 w-3.5" /> Import Leads
            </button>
            <button onClick={exportCsv} data-testid="leads-export" className="btn-outline-lux inline-flex items-center gap-2 px-5 py-2.5 text-[10px]">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
        </div>
        {analytics && (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
              <div className="rounded-xl bg-white/70 p-4 text-center">
                <p className="font-serif text-2xl font-semibold text-[#1A1615]" data-testid="leads-total">{analytics.total}</p>
                <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#91837C]">Total Leads</p>
              </div>
              {STAGES.slice(0, 4).map((st) => (
                <div key={st} className="rounded-xl bg-white/70 p-4 text-center">
                  <p className="font-serif text-2xl font-semibold text-[#1A1615]" data-testid={`leads-funnel-${st}`}>{analytics.funnel[st] || 0}</p>
                  <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#91837C]">{st}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#91837C]">Last 14 days</p>
                <div className="mt-3 flex h-20 items-end gap-1" data-testid="leads-daily-chart">
                  {(analytics.daily || []).map((d) => (
                    <div key={d.date} className="flex-1 rounded-t bg-gradient-to-t from-[#D4AF37]/40 to-[#D4AF37]" style={{ height: `${Math.max(6, (d.count / maxDaily) * 100)}%` }} title={`${d.date}: ${d.count}`} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#91837C]">Demand by treatment</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="leads-top-treatments">
                  {(analytics.top_treatments || []).map((t) => (
                    <span key={t.name} className="rounded-full border border-[#E8D5CE] bg-white/70 px-3 py-1.5 text-[11px] font-medium text-[#5A504B]">
                      {t.name} <span className="text-[#B38B2D]">×{t.count}</span>
                    </span>
                  ))}
                  {!(analytics.top_treatments || []).length && <p className="text-xs font-light text-[#91837C]">Demand data appears as enquiries arrive.</p>}
                </div>
                <p className="mt-4 text-xs font-light text-[#91837C]">
                  Completion rate: <span className="font-semibold text-[#1A1615]" data-testid="leads-completion-rate">{analytics.completion_rate}%</span> of leads reached completed appointments
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* demand radar */}
      <div className="glass rounded-2xl p-6" data-testid="demand-radar">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B38B2D]">
              <Radar className="h-5 w-5 text-white" />
            </span>
            <div>
              <h2 className="font-serif text-xl font-semibold text-[#1A1615]">AI Demand Radar</h2>
              <p className="text-xs font-light text-[#91837C]">Reads your real enquiry demand and proposes the next keywords, articles and campaign angles.</p>
            </div>
          </div>
          <button onClick={runRadar} disabled={radarBusy} data-testid="radar-generate" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
            {radarBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {radar ? "Refresh Radar" : "Run the Radar"}
          </button>
        </div>
        {radar && (
          <div className="mt-5 grid gap-5 lg:grid-cols-3" data-testid="radar-results">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Next keywords</p>
              <ul className="mt-3 space-y-2">
                {(radar.keywords || []).map((k) => (
                  <li key={k} className="rounded-lg bg-white/70 px-3 py-2 text-xs font-medium text-[#5A504B]">{k}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Article ideas</p>
              <ul className="mt-3 space-y-2">
                {(radar.article_ideas || []).map((a) => (
                  <li key={a} className="rounded-lg bg-white/70 px-3 py-2 text-xs font-light text-[#5A504B]">{a}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Outreach angles</p>
              <ul className="mt-3 space-y-2">
                {(radar.outreach_angles || []).map((o) => (
                  <li key={o} className="rounded-lg bg-white/70 px-3 py-2 text-xs font-light text-[#5A504B]">{o}</li>
                ))}
              </ul>
            </div>
            <p className="text-[10px] font-light text-[#91837C] lg:col-span-3">
              Based on {radar.based_on} enquiries · generated {radar.generated_at ? new Date(radar.generated_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
            </p>
          </div>
        )}
      </div>

      {/* social capture */}
      <div className="glass rounded-2xl p-6" data-testid="social-capture">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#E1306C] to-[#833AB4]">
              <Instagram className="h-5 w-5 text-white" />
            </span>
            <div>
              <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Instagram Comment Leads</h2>
              <p className="text-xs font-light text-[#91837C]">
                Scans comments on your own posts for buying signals ("price?", "how do I book?") and captures them as leads — official Meta API, zero account risk.
              </p>
            </div>
          </div>
          <button onClick={scanInstagram} disabled={scanBusy} data-testid="social-scan-button" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
            {scanBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Instagram className="h-3.5 w-3.5" />}
            Scan Comments
          </button>
        </div>
        {scanResult && !scanResult.scanned && (
          <p className="mt-4 rounded-xl bg-[#FAF3E3] px-4 py-3 text-xs font-medium text-[#8a6d1f]" data-testid="social-scan-reason">{scanResult.reason}</p>
        )}
        {scanResult?.scanned && (
          <p className="mt-4 rounded-xl bg-white/70 px-4 py-3 text-xs font-light text-[#5A504B]" data-testid="social-scan-summary">
            Scanned {scanResult.comments_seen} comments · {scanResult.intent_comments} showed enquiry intent · <span className="font-semibold text-[#1A1615]">{scanResult.new_leads} new lead{scanResult.new_leads === 1 ? "" : "s"}</span> added to the pipeline below
          </p>
        )}
        <p className="mt-4 text-[10px] font-light text-[#91837C]">
          LinkedIn and other platforms don't permit automated reading — gather those leads manually and bring them in with{" "}
          <button type="button" onClick={() => setImportOpen(true)} data-testid="social-import-link" className="font-semibold text-[#B38B2D] underline underline-offset-2">
            Import Leads
          </button>
          .
        </p>
      </div>

      {/* pipeline */}
      <div className="glass rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1615]">
            <Target className="h-5 w-5 text-[#B38B2D]" /> Lead Pipeline
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="leads-stage-filters">
            {["all", ...STAGES].map((st) => (
              <button
                key={st}
                onClick={() => setStage(st)}
                data-testid={`leads-filter-${st}`}
                className={`rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                  stage === st ? "bg-[#1A1615] text-white" : "border border-[#E8D5CE] text-[#5A504B] hover:border-[#B38B2D]"
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-5 space-y-3" data-testid="leads-pipeline">
          {filtered.map((l) => (
            <LeadCard key={l.id} lead={l} />
          ))}
          {!filtered.length && <p className="py-8 text-center text-sm font-light text-[#91837C]">No leads at this stage yet.</p>}
        </div>
      </div>

      {/* import modal */}
      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1615]/50 p-4 backdrop-blur-sm" data-testid="leads-import-modal">
          <div className="glass w-full max-w-lg rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold text-[#1A1615]">Import Leads</h3>
              <button onClick={() => setImportOpen(false)} data-testid="leads-import-cancel" className="text-[#91837C] transition-colors hover:text-[#1A1615]" aria-label="Close import">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-xs font-light text-[#91837C]">
              One lead per line: <span className="font-medium text-[#5A504B]">name, phone, email, treatment, note</span> — only a name or phone is required. Duplicates are skipped automatically.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <select value={importSource} onChange={(e) => setImportSource(e.target.value)} data-testid="leads-import-source" className="input-lux text-xs">
                <option value="manual">Manual / Walk-in</option>
                <option value="linkedin">LinkedIn</option>
                <option value="instagram_dm">Instagram DM</option>
                <option value="referral">Referral</option>
                <option value="other">Other</option>
              </select>
              <label className="btn-outline-lux inline-flex cursor-pointer items-center gap-2 px-4 py-2 text-[10px]">
                <FileUp className="h-3.5 w-3.5" /> Choose .csv
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  className="hidden"
                  data-testid="leads-import-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => setImportText(String(reader.result || ""));
                    reader.readAsText(f);
                  }}
                />
              </label>
            </div>
            <textarea
              rows={7}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              data-testid="leads-import-input"
              placeholder={"Ayesha Khan, +971501112233, ayesha@example.com, BB Laser, Asked about pricing\nOmar R, +971559988776"}
              className="input-lux mt-4 w-full resize-none font-mono text-xs"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setImportOpen(false)} className="btn-outline-lux px-5 py-2.5 text-[10px]">Cancel</button>
              <button onClick={doImport} disabled={importBusy} data-testid="leads-import-submit" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
                {importBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
                Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
